import { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../util/toasts';
import { ConfigSection } from '../components/config-property';
import { Spinner } from '../../../components/spinner/spinner';
import { DateTime } from 'luxon';
import { Typography } from '../../../components/typography/typography';
import { getOrdinalSuffix } from '../../date-time-advanced/date-time-utils';
import { SelectForm } from '../../../components/select-form/select-form';

export const UnpaidBatchConfig = () => {
  const { updateConfiguration, getMultipleConfigurations } = useAPI();

  const [loading, setLoading] = useState<boolean>(true);
  const [timeZone, setTimeZone] = useState<string>();

  const [sendTime, setSendTime] = useState<DateTime>();

  const [dayOfMonth, setDayOfMonth] = useState<number>();
  const [hour, setHour] = useState<number>();

  useEffect(() => {
    if (dayOfMonth !== undefined && hour !== undefined) {
      const newSendTime = DateTime.utc()
        .setZone(timeZone)
        .set({ day: dayOfMonth, hour, month: 1, minute: 0, second: 0 });
      setSendTime(newSendTime);
      doUpdate(newSendTime);
    } else if (!loading && (dayOfMonth === undefined || hour === undefined)) {
      setSendTime(undefined);
      doUpdate(undefined);
    } else {
      setSendTime(undefined);
    }
  }, [dayOfMonth, hour]);

  const generateDayOptions = () => {
    const options = [];
    for (let day = 1; day <= 31; day++) {
      options.push({
        value: day,
        label: `${day}${getOrdinalSuffix(day)}`,
      });
    }
    return options;
  };

  const dayOptions = generateDayOptions();

  const formatHour = (hour?: number) => {
    if (hour === undefined) {
      return 'Select Hour';
    }
    const period = hour < 12 ? 'AM' : 'PM';
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${hour12} ${period}`;
  };

  const generateHourOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      options.push({
        value: hour,
        label: formatHour(hour),
      });
    }
    return options;
  };

  const hourOptions = generateHourOptions();

  const doUpdate = (sendTime: DateTime | undefined) => {
    updateConfiguration('CONF_UNPAID_EMAIL_SEND_DATE', sendTime ? sendTime.toUTC().toISO() : '').catch(() => {
      toast('Failed to update unpaid batch configuration', 'error');
    });
  };

  useEffect(() => {
    setLoading(true);

    getMultipleConfigurations(['CONF_UNPAID_EMAIL_SEND_DATE', 'CONF_TIMEZONE'])
      .then((configs) => {
        const timeZone = configs.find((config) => config.variable === 'CONF_TIMEZONE')?.value;
        setTimeZone(timeZone);

        const emailSendDate = configs.find((config) => config.variable === 'CONF_UNPAID_EMAIL_SEND_DATE');

        const dateObj = emailSendDate?.value ? DateTime.fromISO(emailSendDate.value) : undefined;

        setSendTime(emailSendDate?.value ? DateTime.fromISO(emailSendDate.value).setZone(timeZone) : undefined);
        setDayOfMonth(dateObj ? dateObj.day : undefined);
        setHour(dateObj ? dateObj.hour : undefined);
      })
      .catch(() => toast('Failed to load unpaid batch config', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const emailStatus = emailStatusFromSendTime(sendTime);

  return !loading ? (
    <ConfigSection>
      <div>Select Date and Time for Statements to be Sent Each Month</div>
      <SelectForm
        value={{
          value: dayOfMonth,
          label: dayOfMonth ? `${dayOfMonth}${getOrdinalSuffix(dayOfMonth)}` : 'Select Day',
        }}
        onChange={(v) => {
          setDayOfMonth(v?.value as number);
        }}
        options={dayOptions}
        placeholder="Select Day"
        isClearable={true}
      />
      <SelectForm
        value={{
          value: hour,
          label: formatHour(hour),
        }}
        onChange={(v) => {
          setHour(v?.value as number);
        }}
        options={hourOptions}
        placeholder="Select Hour"
        isClearable={true}
      />
      <Typography type="text-sm">{emailStatus}</Typography>
    </ConfigSection>
  ) : (
    <Spinner />
  );
};

const emailStatusFromSendTime = (sendTime: DateTime | undefined) => {
  if (!sendTime) {
    return 'No email will be sent.';
  } else {
    return `Email will be sent at ${sendTime.toLocaleString(DateTime.TIME_SIMPLE)} on the ${
      sendTime.day
    }${getOrdinalSuffix(
      sendTime.day,
    )} day or the last day of the month, if the numeric day is greater than the last day of the month.`;
  }
};
